import type { RunExecutionStatus } from "../../shared/executions/executionListTypes";
import { useFormatRelativeTime } from "../hooks/useFormatRelativeTime";
import { useMakeUrl } from "../lib/urls";
import { Dropdown } from "./Dropdown";
import { ExecutionSidebar, ExecutionSidebarRow } from "./ExecutionSidebar";
import { HeadingDropdownOpener } from "./HeadingDropdownOpener";
import { Icon } from "./Icon";
import { MetaSep } from "./MetaSeparator";
import { OpenerLabel, pinPanelSection, sidePanelWidth } from "./PinnedPanel";
import { ExecutionNavTarget } from "./StepExecutionNav";
import { formatTime } from "./StepExecutions";
import { Text } from "./Text";

type Run = { id: string; createdAt: number };
type Statuses = ReadonlyMap<string, RunExecutionStatus["status"]>;

function RunStatus({ run, statuses }: { run: Run; statuses: Statuses }) {
  const formatRelativeTime = useFormatRelativeTime();
  const status = statuses.get(run.id) ?? "running";
  return (
    <>
      <span
        style={{
          color:
            status === "success"
              ? "var(--green-600)"
              : status === "error"
                ? "var(--red-600)"
                : undefined,
        }}
      >
        {status === "success"
          ? "Successful"
          : status === "error"
            ? "Failed"
            : "Running"}
      </span>
      <MetaSep />
      {formatRelativeTime(run.createdAt)}
    </>
  );
}

export function WorkflowRunSidebar({
  runs,
  selectedRunId,
  statuses,
  loading,
  onCollapse,
}: {
  runs: readonly Run[];
  selectedRunId: string | null;
  statuses: Statuses;
  loading: boolean;
  onCollapse: () => void;
}) {
  const makeUrl = useMakeUrl();
  const selectedIndex = runs.findIndex((run) => run.id === selectedRunId);

  return (
    <ExecutionSidebar
      selectedId={selectedRunId ?? undefined}
      loading={loading}
      empty={runs.length === 0}
      onCollapse={onCollapse}
      actions={
        selectedIndex >= 0 && (
          <>
            <ExecutionNavTarget
              kind="arrow"
              parameter="run"
              id={runs[selectedIndex + 1]?.id}
              label="Previous run"
            >
              <Icon name="arrow-down" size={12} />
            </ExecutionNavTarget>
            <ExecutionNavTarget
              kind="arrow"
              parameter="run"
              id={runs[selectedIndex - 1]?.id}
              label="Next run"
            >
              <Icon name="arrow-up" size={12} />
            </ExecutionNavTarget>
          </>
        )
      }
    >
      {runs.map((run) => (
        <ExecutionSidebarRow
          key={run.id}
          to={makeUrl(null, { run: run.id })}
          selected={run.id === selectedRunId}
          label={formatTime(run.createdAt)}
          icon={<Icon name="run" size={12} />}
        >
          <RunStatus run={run} statuses={statuses} />
        </ExecutionSidebarRow>
      ))}
    </ExecutionSidebar>
  );
}

export function WorkflowRunPicker({
  runs,
  selectedRunId,
  statuses,
  pinned,
  onTogglePinned,
}: {
  runs: readonly Run[];
  selectedRunId: string | null;
  statuses: Statuses;
  pinned: boolean;
  onTogglePinned: () => void;
}) {
  const makeUrl = useMakeUrl();
  const selectedRun = runs.find((run) => run.id === selectedRunId);
  return (
    <Dropdown
      maxHeight={400}
      minWidth={sidePanelWidth}
      maxWidth={sidePanelWidth}
      opener={
        <HeadingDropdownOpener aria-label="Executions">
          <Icon name={selectedRun ? "run" : "history"} size={12} />
          <OpenerLabel>
            <Text variant="text-small-md" ellipsis>
              {selectedRun ? formatTime(selectedRun.createdAt) : "Executions"}
            </Text>
          </OpenerLabel>
          <Icon name="chevron-down" size={12} />
        </HeadingDropdownOpener>
      }
      sections={[
        {
          label: "Executions",
          items: runs.map((run) => ({
            type: "link" as const,
            compact: true,
            key: run.id,
            to: makeUrl(null, { run: run.id }),
            label: formatTime(run.createdAt),
            subtitle: (
              <Text variant="text-small" color="var(--text-secondary)">
                <RunStatus run={run} statuses={statuses} />
              </Text>
            ),
            left: <Icon name="run" size={12} />,
            selected: run.id === selectedRunId,
            right:
              run.id === selectedRunId ? (
                <Icon name="check" size={12} color="var(--text-secondary)" />
              ) : undefined,
          })),
        },
        pinPanelSection({
          label: "Pin executions panel",
          side: "right",
          pinned,
          onToggle: onTogglePinned,
        }),
      ]}
    />
  );
}
